// components/RequesterForm.js
import React, { useState, useEffect } from 'react';
import {
    Paper,
    Typography,
    Grid,
    TextField,
    Button,
    RadioGroup,
    FormControlLabel,
    Radio,
    CircularProgress,
    FormControl,
    FormLabel,
    Box
} from '@mui/material';
import SimpleAutoComplete from '../../../components/auto-complete/autocomplete-categories';
import SimpleAutoCompleteMultiple from '../../../components/auto-complete/autocomplete-multiple-create';
import SimpleCheckBox from '../../../components/checkbox/check';
import * as CalledService from "../../../services/called.service";
import Api from '../../../services/api';
import CustomizedSnackbars from '../../../components/material-snackbars';

const RequesterForm = ({ 
    organizationList, 
    organizationCategory,
    userSession,
    onComplete,
    onCancel,
    intl
}) => {
    const [requesterOption, setRequesterOption] = useState('me');
    const [selectedOrganization, setSelectedOrganization] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [clientUsersList, setClientUsersList] = useState([]);
    const [loadingClientUsers, setLoadingClientUsers] = useState(false);
    const [showNewUserForm, setShowNewUserForm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [associateOrganizationOptions, setAssociateOrganizationOptions] = useState('associateSelectedOrganization');
    const [OrganizationFilter, setOrganizationFilter] = useState([]);
    const [selectOrganizations, setSelectOrganizations] = useState(false);
    const [createUserCalled, setCreateUserCalled] = useState({
        nameOfNewUser: null,
        cpfOfNewUser: null,
        emailOfNewUser: null,
        confirmEmailOfNewUser: null
    });
    const [itemError, setItemError] = useState({ document: '' });
    const [openNotification, setOpenNotification] = useState(false);
    const [notificationVariant, setNotificationVariant] = useState('success');
    const [notificationMessage, setNotificationMessage] = useState('');

    const showNotification = (variant, message) => {
        setNotificationVariant(variant);
        setNotificationMessage(message);
        setOpenNotification(true);
        setTimeout(() => setOpenNotification(false), 5000);
    };

    const closeNotification = () => {
        setOpenNotification(false);
    };

    const getClientsByOrganizationId = async (orgId) => {
        setLoadingClientUsers(true);
        const result = await CalledService.getClientsByOrganizationId(orgId, true);
        if (result.success) {
            let reordernewarrayuserteclist = result.data.map(item => ({
                ...item,
                name: `${item.name} (${item.email})`
            }));
            setClientUsersList(reordernewarrayuserteclist);
        }
        setLoadingClientUsers(false);
    };

    useEffect(() => {
        if (!organizationList || organizationList.length === 0) return;

        const orgIdFromCategory = organizationCategory && organizationCategory.organizationId ? organizationCategory.organizationId : null;

        const orgToSelect = orgIdFromCategory
            ? organizationList.find(o => String(o.id) === String(orgIdFromCategory))
            : organizationList[0];

        if (!orgToSelect) return;

        setSelectedOrganization(orgToSelect);
        getClientsByOrganizationId(orgToSelect.id);
        
    }, [organizationList, organizationCategory]);

    useEffect(() => {
        if (requesterOption === 'other' && selectedOrganization) {
            getClientsByOrganizationId(selectedOrganization.id);
        }
    }, [requesterOption, selectedOrganization]);

    useEffect(() => {
        if (selectOrganizations) {
            setOrganizationFilter(organizationList);
        } else {
            setOrganizationFilter([]);
        }
    }, [selectOrganizations, organizationList]);

    const cpfMask = (value) => {
        return value
            .replace(/\D/g, '')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d{1,2})/, '$1-$2')
            .replace(/(-\d{2})\d+?$/, '$1');
    };

    const CNPJMask = (valor) => {
    const cleaned = valor.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    return cleaned
        .replace(/^(.{2})(.)/,        '$1.$2')
        .replace(/^(.{6})(.)/,        '$1.$2')
        .replace(/^(.{10})(.)/,       '$1/$2')
        .replace(/^(.{15})(.{1,2})$/, '$1-$2')
        .substring(0, 18);
};

    const isCPFOrCNPJValid = (value) => {
        const numericValue = value.replace(/\D/g, '');
        
        if (numericValue.length === 11) {
            // Validação CPF
            let sum = 0;
            let remainder;
            for (let i = 1; i <= 9; i++) {
                sum += parseInt(numericValue.substring(i - 1, i)) * (11 - i);
            }
            remainder = (sum * 10) % 11;
            if (remainder === 10 || remainder === 11) remainder = 0;
            if (remainder !== parseInt(numericValue.substring(9, 10))) return false;
            
            sum = 0;
            for (let i = 1; i <= 10; i++) {
                sum += parseInt(numericValue.substring(i - 1, i)) * (12 - i);
            }
            remainder = (sum * 10) % 11;
            if (remainder === 10 || remainder === 11) remainder = 0;
            return remainder === parseInt(numericValue.substring(10, 11));
        } else if (value.replace(/[.\-/]/g, '').length === 14) {
            const cleaned = value.replace(/[.\-/]/g, '').toUpperCase();
            const basePart = cleaned.substring(0, 12);
            const dvPart = cleaned.substring(12);


            if (!/^\d{2}$/.test(dvPart)) return false;

            const charToValue = (c) => c.charCodeAt(0) - 48;
            const calcDV = (seq) => {
                let sum = 0, peso = 2;
                for (let i = seq.length - 1; i >= 0; i--) {
                    sum += charToValue(seq[i]) * peso;
                    peso = peso === 9 ? 2 : peso + 1;
                }
                const resto = sum % 11;
                return (resto === 0 || resto === 1) ? 0 : 11 - resto;
            };

            const dv1 = calcDV(basePart);
            if (dv1 !== parseInt(dvPart[0])) return false;
            const dv2 = calcDV(basePart + dv1.toString());
            return dv2 === parseInt(dvPart[1]);
        }
        return false;
    };

    const handleInputChangeNewUser = (e) => {
        const { name, value } = e.target;
        
        if (name === 'cpfOfNewUser') {
            let valor = value.length <= 14 ? cpfMask(value) : CNPJMask(value);
            setCreateUserCalled(prev => ({ ...prev, [name]: valor }));
            
            if (valor.length === 0) {
                setItemError({ document: 'Campo obrigatório' });
            } else if ((value.replace(/[.\-/]/g, '').length !== 14 && value.replace(/[.\-/]/g, '').length !== 11) || !isCPFOrCNPJValid(value)) {
                setItemError({ document: 'CPF/CNPJ inválido' });
            } else {
                setItemError({ document: '' });
            }
        } else {
            setCreateUserCalled(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async () => {
        if (showNewUserForm) {
            const { nameOfNewUser, cpfOfNewUser, emailOfNewUser, confirmEmailOfNewUser } = createUserCalled;
            
            if (!nameOfNewUser) {
                showNotification("error", "Nome é obrigatório");
                return;
            }
            if (!cpfOfNewUser || !isCPFOrCNPJValid(cpfOfNewUser)) {
                showNotification("error", "CPF/CNPJ é obrigatório e deve ser válido");
                return;
            }
            if (!emailOfNewUser) {
                showNotification("error", "Email é obrigatório");
                return;
            }
            if (emailOfNewUser !== confirmEmailOfNewUser) {
                showNotification("error", "Emails não conferem");
                return;
            }

            setLoading(true);

            let data = new FormData();
            data.append("username", nameOfNewUser);
            data.append("userdocument", cpfOfNewUser);
            data.append("useremail", emailOfNewUser);
            data.append("userconfirmemail", confirmEmailOfNewUser);
            
            if (associateOrganizationOptions === 'associateOtherOrganizations') {
                const organizationsIds = OrganizationFilter.map(item => item.id);
                data.append("AssociateOrganizationOption", 2);
                data.append("SelectedOrganizations", organizationsIds.join(','));
            } else {
                data.append("AssociateOrganizationOption", 2);
                data.append("SelectedOrganizations", selectedOrganization ? selectedOrganization.id : '');
            }
            
            data.append("OrganizationId", organizationCategory.organizationId);
            data.append("CreatedAt", new Date().toISOString());
            data.append("CreatedUser", userSession.user.id);
            
            try {
                const response = await Api.post("/users/create-user-called", data);
                if (response.data && response.data.success) {
                    const newUser = {
                        ...response.data.data,
                        name: `${response.data.data.name} (${response.data.data.email})`
                    };
                    onComplete({ user: newUser, userId: newUser.id });
                }
            } catch (error) {
                showNotification("error", "Erro de conexão. Tente novamente.");
            } finally {
                setLoading(false);
            }
        } else {
            if (requesterOption === 'other' && !selectedUser) {
                showNotification("error", "Selecione um solicitante");
                return;
            }
            
            const userToSend = requesterOption === 'me' ? userSession.user : selectedUser;
            const formattedUser = {
                ...userToSend,
                name: userToSend.email ? `${userToSend.name} (${userToSend.email})` : userToSend.name
            };
            onComplete({ user: formattedUser, userId: formattedUser.id });
        }
    };


    return (
        <Paper elevation={3} style={{ padding: '2rem', maxWidth: 800, minWidth:500, margin: '0 auto' }}>
            <Typography variant="h5" gutterBottom align="center">
                {intl.formatMessage({ id: "requester.selection" }, { defaultValue: "Quem é o solicitante?" })}
            </Typography>
            {/* <Typography variant="body2" color="textSecondary" align="center" gutterBottom>
                {intl.formatMessage({ id: "requester.selection.step.description" }, { defaultValue: "Passo 1 de 2 - Identifique quem está solicitando" })}
            </Typography> */}
            
            <RadioGroup
                value={requesterOption}
                onChange={(e, value) => {
                    const v = typeof value !== 'undefined' ? value : (e && e.target ? e.target.value : null);
                    if (v == null) return;
                    setRequesterOption(v);
                    setShowNewUserForm(false);
                    // selectedOrganization
                    setSelectedUser(null);
                    setClientUsersList([]);
                }}
                style={{ marginTop: '1.5rem' }}
            >
                <FormControlLabel value="me" control={<Radio />} label={intl.formatMessage({ id: "i.am.requester" }, { defaultValue: "Eu sou o solicitante" })} />
                <FormControlLabel value="other" control={<Radio />} label={intl.formatMessage({ id: "choose.other.requester" }, { defaultValue: "Vou escolher outro solicitante" })} />
            </RadioGroup>

            {requesterOption === 'other' && (
                <Box mt={2}>
                    {selectedOrganization && (
                        <>
                            {!showNewUserForm ? (
                                <>
                                    {loadingClientUsers ? (
                                        <Box display="flex" justifyContent="center" mt={2}>
                                            <CircularProgress size={30} />
                                        </Box>
                                    ) : (
                                        <>
                                            <Box mt={2}>
                                                <SimpleAutoComplete
                                                    label={intl.formatMessage({ id: "select.requester" }, { defaultValue: "Selecionar solicitante" })}
                                                    options={clientUsersList.map(user => ({
                                                        id: user.id,
                                                        name: user.name
                                                    }))}
                                                    stateName="selectedUser"
                                                    changeSelect={(stateName, value) => setSelectedUser(value)}
                                                    selected={selectedUser}
                                                    fullWidth
                                                />
                                            </Box>
                                            
                                            {clientUsersList.length === 0 && (
                                                <Typography variant="body2" color="textSecondary" align="center" mt={1}>
                                                    {intl.formatMessage({ id: "no.users.found" }, { defaultValue: "Nenhum usuário encontrado nesta organização" })}
                                                </Typography>
                                            )}
                                            
                                            <Box display="flex" justifyContent="center" mt={2}>
                                                <Button 
                                                    color="primary"
                                                    onClick={() => setShowNewUserForm(true)}
                                                >
                                                    + {intl.formatMessage({ id: "register.new.requester" }, { defaultValue: "Cadastrar novo solicitante" })}
                                                </Button>
                                            </Box>
                                        </>
                                    )}
                                </>
                            ) : (
                                <>
                                    <Box display="flex" justifyContent="flex-end" mb={2}>
                                        <Button 
                                            size="small"
                                            onClick={() => {
                                                setShowNewUserForm(false);
                                                setCreateUserCalled({
                                                    nameOfNewUser: null,
                                                    cpfOfNewUser: null,
                                                    emailOfNewUser: null,
                                                    confirmEmailOfNewUser: null
                                                });
                                            }}
                                        >
                                            ← {intl.formatMessage({ id: "back.to.list" }, { defaultValue: "Voltar para lista" })}
                                        </Button>
                                    </Box>
                                    
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField 
                                                fullWidth 
                                                label={`${intl.formatMessage({ id: "name" })}*`} 
                                                inputProps={{ maxLength: 255 }} 
                                                variant='outlined' 
                                                name='nameOfNewUser' 
                                                onChange={handleInputChangeNewUser}  
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField  
                                                name="cpfOfNewUser"
                                                label="CPF/CNPJ*"
                                                fullWidth
                                                value={createUserCalled.cpfOfNewUser}
                                                onChange={handleInputChangeNewUser}
                                                error={!!itemError?.document}
                                                helperText={itemError?.document}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField 
                                                fullWidth 
                                                label={`${intl.formatMessage({ id: "email" })}*`} 
                                                inputProps={{ maxLength: 255 }} 
                                                variant='outlined' 
                                                name='emailOfNewUser' 
                                                onChange={handleInputChangeNewUser}  
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField 
                                                fullWidth 
                                                label={`${intl.formatMessage({ id: "confirm.email" })}*`} 
                                                inputProps={{ maxLength: 255 }} 
                                                variant='outlined' 
                                                name='confirmEmailOfNewUser' 
                                                onChange={handleInputChangeNewUser}  
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormControl>
                                                <FormLabel>{intl.formatMessage({ id: "associate.organizations" })}</FormLabel>
                                                <RadioGroup
                                                    value={associateOrganizationOptions}
                                                    onChange={(e) => {
                                                        setAssociateOrganizationOptions(e.target.value);
                                                        if (e.target.value === 'associateSelectedOrganization') {
                                                            setOrganizationFilter([]);
                                                            setSelectOrganizations(false);
                                                        }
                                                    }}
                                                >
                                                    <FormControlLabel 
                                                        value="associateSelectedOrganization" 
                                                        control={<Radio />} 
                                                        label={intl.formatMessage({ id: "associate.requesting.user.to.selected.organization" })} 
                                                    />
                                                    <FormControlLabel 
                                                        value="associateOtherOrganizations" 
                                                        control={<Radio />} 
                                                        label={intl.formatMessage({ id: "associate.requesting.user.to.others.organizations" })} 
                                                    />
                                                </RadioGroup>
                                            </FormControl>
                                            
                                            {associateOrganizationOptions === 'associateOtherOrganizations' && (
                                                <>
                                                    <Typography variant="body2" color="textSecondary" gutterBottom>
                                                        {intl.formatMessage({ id: "pre.selected.organization" })}: {selectedOrganization?.name}
                                                    </Typography>
                                                    <SimpleAutoCompleteMultiple 
                                                        label={intl.formatMessage({ id: "organization" })} 
                                                        options={organizationList} 
                                                        stateName='OrganizationFilter' 
                                                        changeSelect={(stateName, value) => setOrganizationFilter(value)} 
                                                        selected={OrganizationFilter}
                                                        placeholder={intl.formatMessage({ id: "or.select.other.organizations.optional" })}
                                                    />
                                                    <Grid item xs={12}>
                                                        <SimpleCheckBox 
                                                            label={intl.formatMessage({ id: "technical.team.edit.select.all.organizations" })} 
                                                            name='selectOrganizations' 
                                                            stateName='selectOrganizations' 
                                                            changeSelect={(stateName, value) => setSelectOrganizations(value)} 
                                                            selected={selectOrganizations} 
                                                        />
                                                    </Grid>
                                                </>
                                            )}
                                        </Grid>
                                    </Grid>
                                </>
                            )}
                        </>
                    )}
                </Box>
            )}

            <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
                <Button 
                    variant="outlined" 
                    onClick={onCancel}
                >
                    {intl.formatMessage({ id: "cancel" })}
                </Button>
                <Button 
                    variant="contained" 
                    color="primary"
                    onClick={handleSubmit}
                    disabled={loading || (requesterOption === 'other' && !selectedUser && !showNewUserForm)}
                >
                    {loading ? <CircularProgress size={24} /> : intl.formatMessage({ id: "continue" }, { defaultValue: "Continuar" })}
                </Button>
            </Box>

            <CustomizedSnackbars
                variant={notificationVariant}
                message={notificationMessage}
                isOpen={openNotification}
                toClose={closeNotification}
            />
        </Paper>

            
    );
};

export default RequesterForm;